/**
 * HorarioMedicoModal — Editor de horario personalizado del médico
 * en una sala específica.
 *
 * Permite configurar casos como:
 *   "Dr. Valverde en Medicina General SOLO Lunes y Viernes 8-10am"
 *
 * Modelo:
 *   - Toggle `usaHorarioSala` (true = hereda · false = horario propio)
 *   - Si false: subcolección `salas/{salaId}/medicos_sala/{medicoId}/horarios`,
 *     editada con el MISMO componente que la pestaña Horarios (vista Semana /
 *     Lista) reutilizando `HorariosEditor` en modo médico.
 */
import { useState, useEffect } from 'react';
import { doc, getDoc } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { Clock, Info } from 'lucide-react';
import { db, functions } from '../../config/firebase';
import { Modal } from '../ui/Modal';
import { useToast } from '../../contexts/ToastContext';
import { HorariosEditor } from './HorariosEditor';
import styles from './HorarioMedicoModal.module.css';

const callActualizarConfig = httpsCallable(
  functions,
  'portal_actualizarConfiguracionMedicoSala',
);

export interface HorarioMedicoModalProps {
  open: boolean;
  onClose: () => void;
  salaId: string;
  medicoId: string;
  medicoNombre: string;
}

export function HorarioMedicoModal({
  open,
  onClose,
  salaId,
  medicoId,
  medicoNombre,
}: HorarioMedicoModalProps) {
  const { addToast } = useToast();

  const [usaHorarioSala, setUsaHorarioSala] = useState<boolean>(true);
  const [loadingConfig, setLoadingConfig] = useState(true);
  const [toggling, setToggling] = useState(false);

  // Cargar config inicial (usaHorarioSala)
  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    (async () => {
      try {
        const snap = await getDoc(doc(db, `salas/${salaId}/medicos_sala/${medicoId}`));
        if (cancelled) return;
        const data = snap.data();
        setUsaHorarioSala(data?.usaHorarioSala !== false);
      } finally {
        if (!cancelled) setLoadingConfig(false);
      }
    })();
    return () => { cancelled = true; };
  }, [open, salaId, medicoId]);

  // Toggle usaHorarioSala
  const handleToggle = async (next: boolean) => {
    setToggling(true);
    try {
      await callActualizarConfig({ salaId, medicoId, usaHorarioSala: next });
      setUsaHorarioSala(next);
      addToast(
        'success',
        next
          ? 'Médico hereda el horario de la sala'
          : 'Médico ahora usa horario propio. Configure los bloques abajo.',
      );
    } catch (err) {
      addToast('error', err instanceof Error ? err.message : 'Error al actualizar');
    } finally {
      setToggling(false);
    }
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={`Horario de ${medicoNombre}`}
      size="lg"
    >
      <div className={styles.body}>
        {/* Banner explicativo */}
        <div className={styles.banner}>
          <Info size={14} aria-hidden="true" />
          <div>
            Por defecto (interruptor apagado), este médico atiende según el{' '}
            <em>horario de la sala</em> y cubre toda su ventana de operación.
            Active <em>"Horario propio del médico"</em> solo si quiere que
            atienda en bloques específicos que usted defina abajo.
          </div>
        </div>

        {/* Toggle: representa "activar un horario propio del médico". Apagado =
            usa el horario de la sala (default); encendido = bloques propios.
            El interruptor refleja `!usaHorarioSala`, pero el valor guardado y la
            lógica de horario NO cambian (usaHorarioSala: true=sala, false=propio). */}
        <div className={styles.toggleCard}>
          <div className={styles.toggleInfo}>
            <div className={styles.toggleTitle}>
              Horario propio del médico
            </div>
            <div className={styles.toggleHint}>
              {usaHorarioSala
                ? 'Apagado: el médico usa el horario de la sala (disponible en toda la ventana de operación). Actívelo para asignarle un horario propio.'
                : 'Encendido: el médico solo atiende en los bloques específicos que defina abajo.'}
            </div>
          </div>
          <button
            type="button"
            className={`${styles.switch} ${!usaHorarioSala ? styles.switchOn : ''}`}
            onClick={() => handleToggle(!usaHorarioSala)}
            disabled={loadingConfig || toggling}
            role="switch"
            aria-checked={!usaHorarioSala}
          >
            <span className={styles.switchKnob} />
          </button>
        </div>

        {/* Editor de horario propio — mismo diseño que la pestaña Horarios
            (vista Semana / Lista), reutilizando HorariosEditor en modo médico. */}
        {!usaHorarioSala && (
          <div className={styles.editorWrap}>
            <HorariosEditor salaId={salaId} medicoId={medicoId} />
          </div>
        )}

        {usaHorarioSala && (
          <div className={styles.inheritedHint}>
            <Clock size={14} aria-hidden="true" />
            <span>
              Este médico atiende según el horario configurado en la pestaña{' '}
              <em>Horarios</em> de la sala.
            </span>
          </div>
        )}
      </div>
    </Modal>
  );
}
